import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Avatar, Box, Button, Link, Typography } from "@mui/material";

import bg from "../../../../assets/images/payment-link/bg.png";
import {
  AppStoreButtonIcon,
  BackButtonIcon,
  CategoryIcon,
  ExpiryDateIcon,
  PlayStoreButtonIcon,
} from "../../../../assets/icons/MUI/PaymentLinkIcons";

import { PaymentDataResponse } from "../../../../api/types/paymentLink";
import { formatImgUrl } from "../../../../utils/cardFormating";

const CategoryLabel = ({ category }: { category: string }) => {
  return (
    <Box
      component="span"
      sx={{
        borderRadius: "16px",
        border: "1px solid #A5FDD8",
        background: "var(--Success-50, #ECFDF3)",
        mixBlendMode: "multiply",
        color: "var(--Success-700, #027A48)",
        textAlign: "center",
        fontFamily: "Montserrat",
        fontSize: "12px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "18px",

        display: "flex",
        gap: "8px",
        alignItems: "center",
        width: "fit-content",
        padding: "2px 8px",
      }}
    >
      <Box
        component="span"
        sx={{
          borderRadius: "50%",
          width: "8px",
          height: "8px",
          background: "var(--Success-500, #12B76A)",
          textTransform: "capitalize",
        }}
      />
      {category?.toLocaleLowerCase()}
    </Box>
  );
};

const HeaderSection = ({ category }: { category: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
    <CategoryLabel category={category} />
    <Typography
      sx={{
        color: "var(--Dark---1000, #111)",
        fontFamily: "Montserrat",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "24px",
      }}
    >
      Payment
    </Typography>
  </Box>
);

const AmountSection = ({ amount, currency }: { amount: number; currency: string }) => (
  <Box sx={{ display: "flex", alignItems: "flex-end" }}>
    <Typography
      sx={{
        color: "var(--Gray-900, #101828)",
        fontFamily: "Montserrat",
        fontSize: "48px",
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: "60px",
        letterSpacing: "-0.96px",
      }}
    >
      {amount / 100}
    </Typography>
    <Typography
      sx={{
        color: "var(--Gray-600, #475467)",
        fontFamily: "Montserrat",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "24px",
        textTransform: "uppercase",
      }}
    >
      {currency}
    </Typography>
  </Box>
);

const PayToSection = ({ name, media }: { name: string; media: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
    <Typography
      sx={{
        color: "var(--Gray-600, #475467)",
        fontFamily: "Montserrat",
        fontSize: "14px",
        fontStyle: "normal",
        fontWeight: 500,
        lineHeight: "20px",
      }}
    >
      Pay to
    </Typography>
    <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
      <Avatar src={formatImgUrl(media)} sx={{ width: "40px", height: "40px" }} />
      <Typography
        sx={{
          color: "var(--Gray-900, #101828)",
          fontFamily: "Montserrat",
          fontSize: "24px",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "32px",
        }}
      >
        {name}
      </Typography>
    </Box>
  </Box>
);

const PaymentForSection = ({ paymentFor }: { paymentFor: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
    <Typography
      sx={{
        color: "var(--Gray-600, #475467)",
        fontFamily: "Montserrat",
        fontSize: "18px",
        fontStyle: "normal",
        fontWeight: 600,
        lineHeight: "28px",
      }}
    >
      Payment For
    </Typography>
    <Typography
      sx={{
        color: "var(--Gray-600, #475467)",
        fontFamily: "Montserrat",
        fontSize: "16px",
        fontStyle: "normal",
        fontWeight: 400,
        lineHeight: "24px",
      }}
    >
      {paymentFor}
    </Typography>
  </Box>
);

const MetaSection = ({ category, expiryDate }: { category: string; expiryDate: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
    <Box sx={{ display: "flex", gap: "8px" }}>
      <CategoryIcon />
      <Typography
        sx={{
          color: "var(--Dark---1000, #111)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "24px",
          textTransform: "capitalize",
        }}
      >
        {category?.toLocaleLowerCase()} Payment
      </Typography>
    </Box>
    <Box sx={{ display: "flex", gap: "8px" }}>
      <ExpiryDateIcon />
      <Typography
        sx={{
          color: "var(--Dark---1000, #111)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 400,
          lineHeight: "24px",
        }}
      >
        Link Expiry {format(new Date(expiryDate), "dd MMM yyyy")}
      </Typography>
    </Box>
  </Box>
);

const FooterSection = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "16px" }}>
      <Button
        startIcon={<BackButtonIcon />}
        sx={{
          background: "none !important",
          border: "none !important",
          padding: 0,
          color: "var(--Gray-600, #475467)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 600,
          lineHeight: "24px",
        }}
        onClick={() => navigate("/")}
      >
        Back to Goalngo
      </Button>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "8px" }}>
        <Typography
          sx={{
            color: "var(--Dark---1000, #111)",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "20px",
          }}
        >
          Download App
        </Typography>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Link
            sx={{
              background: "none !important",
              border: "none !important",
              padding: 0,
            }}
            href="https://apps.apple.com/eg/app/goalngo/id6443919098"
            target="_blank"
            rel="noopener noreferrer"
          >
            <AppStoreButtonIcon />
          </Link>
          <Link
            sx={{
              background: "none !important",
              border: "none !important",
              padding: 0,
            }}
            href="https://play.google.com/store/apps/details?id=com.goalngo_tech.goalngo&hl=en&pli=1"
            target="_blank"
            rel="noopener noreferrer"
          >
            <PlayStoreButtonIcon />
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

const PaymentSummary = ({
  paymentLink: {
    paymentCategory,
    amount,
    currency,
    expiresAt: expiryDate,
    paymentFor,
    footballEntity: { name: entityName, mediaUrl: entityMedia },
  },
}: {
  paymentLink: PaymentDataResponse;
}) => {
  return (
    <Box
      sx={{
        backgroundImage: `url(${bg})`,
        width: "50%",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "312px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <HeaderSection category={paymentCategory} />
        <AmountSection amount={amount} currency={currency} />
        <PayToSection name={entityName} media={entityMedia} />
        <PaymentForSection paymentFor={paymentFor} />
        <MetaSection category={paymentCategory} expiryDate={expiryDate} />

        <Box sx={{ borderBottom: "1px solid #D9D9D9", width: "100%" }} />

        <FooterSection />
      </Box>
    </Box>
  );
};

export default PaymentSummary;
